import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PrimaryButton from "../../../../../components/PrimaryButton";

const FIELD_GROUPS = [
  {
    title: "Listings & Content",
    description: "Navigation labels for listing-related pages.",
    fields: [
      {
        key: "featured_listings",
        label: "Featured Listings",
        placeholder: "Featured Listings",
        type: "featured-listing",
      },
      {
        key: "free_listings",
        label: "Free Listings",
        placeholder: "Listings",
        type: "listing",
      },
      {
        key: "listings_labels",
        label: "Listings Labels",
        placeholder: "Tags",
        type: "tags",
      },
      { key: "blogs", label: "Blogs", placeholder: "Blogs", type: "blogs" },
      { key: "news", label: "News", placeholder: "News", type: "news" },
      { key: "events", label: "Events", placeholder: "Events", type: "events" },
      { key: "jobs", label: "Jobs", placeholder: "Jobs", type: "jobs" },
      { key: "plans", label: "Plans", placeholder: "Plans", type: "pricing" },
      { key: "deals", label: "Deals", placeholder: "Deals", type: "deals" },
    ],
  },
  {
    title: "Account & Actions",
    description: "Labels for authentication and primary action links.",
    fields: [
      { key: "login", label: "Login", placeholder: "Login", type: "login" },
      {
        key: "register",
        label: "Register",
        placeholder: "Register",
        type: "register",
      },
      {
        key: "sign_in",
        label: "Sign in",
        placeholder: "Sign in",
        type: "sign-in",
        elementOnly: true,
      },
      {
        key: "create_account",
        label: "Create account.",
        placeholder: "Create account.",
        type: "create-account",
        elementOnly: true,
      },
      {
        key: "add_new_business",
        label: "Add New Business",
        placeholder: "Add New Business",
        type: "add-new-business",
      },
      {
        key: "profile",
        label: "Profile",
        placeholder: "Profile",
        type: "profile",
      },
      {
        key: "log_out",
        label: "Logout",
        placeholder: "Logout",
        type: "log-out",
        elementOnly: true,
      },
      {
        key: "not_signed_up",
        label: "Not signed up?",
        placeholder: "Not signed up?",
        type: "not-signed-up",
        elementOnly: true,
      },
    ],
  },
  {
    title: "Pricing & Plans",
    description: "Labels for monthly & annually in pricing",
    fields: [
      {
        key: "monthly",
        label: "Monthly",
        placeholder: "Monthly",
        type: "monthly",
        elementOnly: true,
      },
      {
        key: "annually",
        label: "Annually",
        placeholder: "Annually",
        type: "annually",
        elementOnly: true,
      },
    ],
  },
  {
    title: "Profile",
    description: "Labels for managing custom names in profile",
    fields: [
      {
        key: "about",
        label: "About",
        placeholder: "About",
        type: "about",
        elementOnly: true,
      },
      {
        key: "owned_business",
        label: "Owned Business",
        placeholder: "Owned Business",
        type: "owned-business",
        elementOnly: true,
      },
      {
        key: "listing_requests",
        label: "Listing Requests",
        placeholder: "Listing Requests",
        type: "listing-requests",
        elementOnly: true,
      },
      {
        key: "followed_business",
        label: "Followed Business",
        placeholder: "Followed Business",
        type: "followed-business",
        elementOnly: true,
      },
      {
        key: "applied_jobs",
        label: "Applied Jobs",
        placeholder: "Applied Jobs",
        type: "applied-jobs",
        elementOnly: true,
      },
      {
        key: "events_activity",
        label: "Events Activity",
        placeholder: "Events Activity",
        type: "events-activity",
        elementOnly: true,
      },
      {
        key: "edit_cover_photo",
        label: "Edit Cover Photo",
        placeholder: "Edit Cover Photo",
        type: "edit-cover-photo",
        elementOnly: true,
      },
      {
        key: "edit_profile",
        label: "Edit Profile",
        placeholder: "Edit Profile",
        type: "edit-profile",
        elementOnly: true,
      },
    ],
  },
];

const ALL_FIELDS = FIELD_GROUPS.flatMap((g) => g.fields);

const buildInitialValues = (selectedTheme) => {
  const elements = selectedTheme?.theme_data?.elements || {};
  const savedRoutes = Array.isArray(selectedTheme?.theme_data?.routes)
    ? selectedTheme.theme_data.routes
    : [];

  const routesByType = savedRoutes.reduce((acc, r) => {
    if (r?.type) acc[r.type] = r.title || "";
    return acc;
  }, {});

  return ALL_FIELDS.reduce((acc, f) => {
    const fromElements = elements?.[f.key];
    const fromRoutes = f.type ? routesByType[f.type] : undefined;
    acc[f.key] = (fromElements ?? fromRoutes ?? "").toString();
    return acc;
  }, {});
};

const COLORS = {
  primary: "#344767",
  accent: "#004EEB",
  accentSoft: "#EFF4FF",
  accentBorder: "#B2DDFF",
  border: "#D2D6DA",
  borderHover: "#b0b8c1",
  surface: "#ffffff",
  surfaceMuted: "#f7f7f7",
  text: "#344767",
  textMuted: "#67748e",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${COLORS.border}`,
  borderRadius: "8px",
  fontSize: "14px",
  outline: "none",
  background: COLORS.surface,
  color: COLORS.text,
  boxSizing: "border-box",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};

const Routeshandling = ({ selectedTheme, handelThemeData, buttonLoading }) => {
  const [values, setValues] = useState(() => buildInitialValues(selectedTheme));

  useEffect(() => {
    setValues(buildInitialValues(selectedTheme));
  }, [
    selectedTheme?.theme_id,
    selectedTheme?.theme_data?.routes,
    selectedTheme?.theme_data?.elements,
  ]);

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const filledCount = useMemo(
    () => ALL_FIELDS.filter((f) => (values[f.key] || "").trim()).length,
    [values]
  );

  const handleSave = async () => {
    if (!selectedTheme?.theme_id) {
      toast.error("Theme is not configured for this agency.");
      return;
    }

    const trimmed = ALL_FIELDS.reduce((acc, f) => {
      const v = (values[f.key] || "").trim();
      if (v) acc[f.key] = v;
      return acc;
    }, {});

    const slugify = (v) =>
      v
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

    const routes = ALL_FIELDS.filter(
      (f) => f.type && !f.elementOnly && trimmed[f.key]
    ).map((f) => ({
      title: slugify(trimmed[f.key]),
      type: f.type,
    }));

    const titles = routes.map((r) => r.title);
    const dupTitle = titles.find((t, i) => titles.indexOf(t) !== i);
    if (dupTitle) {
      toast.error(`Duplicate route title: "${dupTitle}". Titles must be unique.`);
      return;
    }

    const existingElements = selectedTheme?.theme_data?.elements || {};
    const elements = { ...existingElements, ...trimmed };
    ALL_FIELDS.forEach((f) => {
      if (!trimmed[f.key]) delete elements[f.key];
    });

    // Keep homepage section H3s in sync with Custom Names.
    const HEADLINE_SYNC = [
      { nameKey: "featured_listings", headlineKey: "featured_listings_headline" },
      { nameKey: "free_listings", headlineKey: "listings_headline" },
      { nameKey: "listings_labels", headlineKey: "listing_labels_headline" },
    ];
    HEADLINE_SYNC.forEach(({ nameKey, headlineKey }) => {
      if (trimmed[nameKey]) {
        elements[headlineKey] = trimmed[nameKey];
      } else {
        delete elements[headlineKey];
      }
    });

    const result = await handelThemeData({ routes, elements });
    if (result?.success) {
      toast.success("Saved successfully");
    } else if (result && result.success === false) {
      toast.error(result.message || "Failed to save");
    }
  };

  return (
    <SoftBox p={3}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        flexWrap="wrap"
        gap={1}
      >
        <SoftBox>
          <SoftTypography
            variant="h5"
            fontWeight="700"
            sx={{ color: COLORS.primary }}
          >
            Custom Names
          </SoftTypography>
          <SoftTypography variant="caption" sx={{ color: COLORS.textMuted }}>
            Customize navigation labels and section headlines. Leave a field blank
            to hide that route.
          </SoftTypography>
        </SoftBox>
        <SoftBox display="flex" alignItems="center" gap={2}>
          <SoftTypography
            variant="caption"
            fontWeight="600"
            sx={{
              color: COLORS.accent,
              background: COLORS.accentSoft,
              border: `1px solid ${COLORS.accentBorder}`,
              padding: "4px 10px",
              borderRadius: "999px",
            }}
          >
            {filledCount} / {ALL_FIELDS.length} fields filled
          </SoftTypography>
          <PrimaryButton type="button" onClick={handleSave} disabled={buttonLoading}>
            {buttonLoading ? "Saving..." : "Save Changes"}
          </PrimaryButton>
        </SoftBox>
      </SoftBox>

      {FIELD_GROUPS.map((group) => (
        <SoftBox
          key={group.title}
          mt={3}
          p={2.5}
          sx={{
            border: `1px solid ${COLORS.border}`,
            borderRadius: "12px",
            background: COLORS.surface,
            boxShadow: "0 1px 2px rgba(52, 71, 103, 0.06)",
          }}
        >
          <SoftBox mb={2}>
            <SoftTypography
              variant="h6"
              fontWeight="600"
              sx={{ fontSize: "15px", color: COLORS.primary }}
            >
              {group.title}
            </SoftTypography>
            <SoftTypography variant="caption" sx={{ color: COLORS.textMuted }}>
              {group.description}
            </SoftTypography>
          </SoftBox>

          <SoftBox
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            }}
            gap={2}
          >
            {group.fields.map((field) => (
              <SoftBox key={field.key}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.5}
                >
                  <SoftTypography
                    variant="caption"
                    fontWeight="600"
                    sx={{ fontSize: "12px", color: COLORS.primary }}
                  >
                    {field.label}
                  </SoftTypography>
                  {field.type && (
                    <SoftTypography
                      variant="caption"
                      sx={{
                        fontSize: "10px",
                        color: COLORS.accent,
                        background: COLORS.accentSoft,
                        border: `1px solid ${COLORS.accentBorder}`,
                        padding: "2px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                        textTransform: "lowercase",
                      }}
                    >
                      {field.type}
                    </SoftTypography>
                  )}
                </SoftBox>
                <input
                  type="text"
                  value={values[field.key] || ""}
                  placeholder={field.placeholder}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.accent;
                    e.target.style.boxShadow = "0 0 0 3px rgba(0, 78, 235, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = COLORS.border;
                    e.target.style.boxShadow = "none";
                  }}
                />
              </SoftBox>
            ))}
          </SoftBox>
        </SoftBox>
      ))}
    </SoftBox>
  );
};

export default Routeshandling;
